import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Grid, Button, Link, Box } from '@mui/material'
import { AnyInput, useValidatedForm } from '../anyform'
import { getSignin2FA, completeSignin2FA } from '../api'
import { GoHome, ExternSurface, Progress } from '../components'
import { AppContext } from '../context'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { User, DoubleFactorAuthenticationRequest } from '../model'
import { RouterParams } from '../router'
import { Code2FAValidator } from '../validators'

export default function SignIn2FAForm({ match }: RouterParams) {
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)
    const validators = useMemo(() => [new Code2FAValidator()], [])

    const [status, setStatus] = useState<string>('loading')
    const [user, setUser] = useState<User>()
    const { bind, register, isValid } =
        useValidatedForm<DoubleFactorAuthenticationRequest>()

    useEffect(() => {
        setStatus('loading')
        getSignin2FA(match.params.code)
            .then((response) => {
                setUser(response.user)
                setStatus('loaded')
            })
            .catch((error) => {
                setStatus(error.message)
            })
    }, [match.params.code])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()

        const request = bind()
        completeSignin2FA(match.params.code, request)
            .then((_) => {
                context.onSignedIn()
            })
            .catch((error) => {
                feedbackContext.showError(error.message)
            })
            .finally(() => {
                feedbackContext.closeBackdrop()
            })
    }

    if (status === 'loading') {
        return <Progress />
    }

    if (status !== 'loaded') {
        return <GoHome message={status} />
    }

    return (
        <ExternSurface
            title={
                translate('auth.2fa.title', {
                    name: user!.first_name,
                }) as string
            }
            description={translate('auth.2fa.text') as string}
        >
            <form autoComplete="off" noValidate onSubmit={onSubmit}>
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item xs={12}>
                        <AnyInput
                            type="text"
                            name="code"
                            label={translate('users.2fa_code') as string}
                            maxLength={6}
                            register={register}
                            validators={validators}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box my={2}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                            >
                                {translate('buttons.signin')}
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs="auto">
                        <Link component={RouterLink} to="/signup">
                            <small>{translate('buttons.signup')}</small>
                        </Link>
                    </Grid>
                    <Grid item xs="auto">
                        <Link component={RouterLink} to="/recover-password">
                            <small>{translate('buttons.recover')}</small>
                        </Link>
                    </Grid>
                </Grid>
            </form>
        </ExternSurface>
    )
}
